<template>
  <div class="ri-page">
    <PageHeader icon="fas fa-procedures">
      <template #title>Rawat Inap</template>
      <template #subtitle
        >Kendali admisi, ketersediaan bed, catatan klinis, order pelayanan, dan
        pemulangan pasien.</template
      >
      <template #actions
        ><button class="primary" @click="openAdmission">
          <i class="fas fa-plus"></i> Admisi Pasien
        </button></template
      >
    </PageHeader>

    <div class="kpis">
      <article>
        <i class="fas fa-bed blue"></i>
        <div>
          <small>Total bed</small><strong>{{ summary.total_beds || 0 }}</strong>
        </div>
      </article>
      <article>
        <i class="fas fa-user-injured purple"></i>
        <div>
          <small>Terisi</small><strong>{{ summary.occupied || 0 }}</strong>
        </div>
      </article>
      <article>
        <i class="fas fa-door-open green"></i>
        <div>
          <small>Tersedia</small><strong>{{ summary.available || 0 }}</strong>
        </div>
      </article>
      <article>
        <i class="fas fa-chart-pie amber"></i>
        <div>
          <small>Okupansi (BOR)</small
          ><strong>{{ summary.occupancy_rate || 0 }}%</strong>
        </div>
      </article>
      <article>
        <i class="fas fa-sign-out-alt red"></i>
        <div>
          <small>Rencana pulang</small
          ><strong>{{ summary.discharge_planning || 0 }}</strong>
        </div>
      </article>
    </div>

    <section class="card">
      <header>
        <div>
          <span class="eyebrow">SENSUS HARIAN</span>
          <h3>Pasien sedang dirawat</h3>
          <p>
            {{ patients.length }} pasien aktif · diperbarui otomatis dari
            transaksi rawat inap
          </p>
        </div>
        <div class="search">
          <i class="fas fa-search"></i
          ><input
            v-model="search"
            placeholder="Cari pasien, nomor admisi, dokter, atau bed"
          />
        </div>
      </header>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Pasien & admisi</th>
              <th>Bed</th>
              <th>DPJP</th>
              <th>Masuk / LOS</th>
              <th>Penjamin</th>
              <th>Status</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in filtered" :key="p.id">
              <td>
                <b>{{ p.patient_name }}</b
                ><small>{{ p.admission_number }}</small>
              </td>
              <td>
                <b>{{ p.bed_name }}</b
                ><small>{{ p.bed_category }}</small>
              </td>
              <td>{{ p.doctor_name || "-" }}</td>
              <td>
                {{ dateTime(p.admitted_at)
                }}<small>Hari ke-{{ p.length_of_stay }}</small>
              </td>
              <td>
                <span class="payer">{{ label(p.payment_type) }}</span>
              </td>
              <td>
                <span :class="['status', p.status]">{{ label(p.status) }}</span>
              </td>
              <td>
                <button class="action" @click="openPatient(p)">
                  <i class="fas fa-notes-medical"></i> Buka
                </button>
              </td>
            </tr>
            <tr v-if="!filtered.length">
              <td colspan="7" class="empty">
                <i class="fas fa-bed"></i
                ><b>Belum ada pasien rawat inap aktif</b
                ><span
                  >Gunakan tombol Admisi Pasien untuk memulai pelayanan.</span
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <div
      class="modal-shell"
      v-if="modal === 'admission'"
      @click.self="closeModal"
    >
      <div class="dialog admission-dialog">
        <div class="dialog-head">
          <div class="head-icon"><i class="fas fa-hospital-user"></i></div>
          <div>
            <span>ADMISI RAWAT INAP</span>
            <h3>Daftarkan pasien ke ruang perawatan</h3>
          </div>
          <button @click="closeModal">×</button>
        </div>
        <div class="form-grid">
          <div class="wide form-field">
            <span class="form-field__label">Pasien</span>
            <button
              type="button"
              class="patient-lookup-field"
              :class="{ 'is-selected': selectedAdmissionPatient }"
              aria-haspopup="dialog"
              @click="openAdmissionPatientLookup"
            >
              <span class="patient-lookup-field__icon" aria-hidden="true">
                <i
                  :class="
                    selectedAdmissionPatient
                      ? 'fas fa-user-check'
                      : 'fas fa-user-injured'
                  "
                ></i>
              </span>
              <span class="patient-lookup-field__content">
                <strong>{{
                  selectedAdmissionPatient
                    ? selectedAdmissionPatient.name
                    : "Cari dan pilih pasien terdaftar"
                }}</strong>
                <small v-if="selectedAdmissionPatient">
                  {{ admissionPatientIdentity(selectedAdmissionPatient) }}
                </small>
                <small v-else>
                  Cari berdasarkan nama, nomor rekam medis, atau NIK
                </small>
              </span>
              <span
                class="patient-lookup-field__action"
                data-icon-contrast="light"
              >
                <i class="fas fa-search" aria-hidden="true"></i>
                <span>{{ selectedAdmissionPatient ? "Ganti" : "Cari pasien" }}</span>
              </span>
            </button>
          </div>
          <label
            >Dokter penanggung jawab<select
              v-model="admission.attending_doctor_id"
              @change="syncDoctor"
            >
              <option value="">Pilih dokter</option>
              <option v-for="d in candidates.doctors" :value="d.id" :key="d.id">
                {{ d.name }} · {{ d.polyclinic_name || "Klinik" }}
              </option>
            </select></label
          >
          <label
            >Bed tersedia<select v-model="admission.bed_id">
              <option value="">Pilih bed</option>
              <option v-for="b in candidates.beds" :value="b.id" :key="b.id">
                {{ b.name }} · {{ b.category }} · {{ money(b.daily_rate) }}/hari
              </option>
            </select></label
          >
          <label
            >Tanggal & jam masuk<input
              type="datetime-local"
              v-model="admission.admitted_at"
          /></label>
          <label
            >Sumber admisi<select v-model="admission.admission_source">
              <option value="outpatient">Rawat jalan</option>
              <option value="emergency">IGD</option>
              <option value="direct">Admisi langsung</option>
              <option value="referral">Rujukan</option>
            </select></label
          >
          <label
            >Penjamin<select v-model="admission.payment_type">
              <option value="general">Umum</option>
              <option value="bpjs">BPJS</option>
              <option value="insurance">Asuransi</option>
              <option value="corporate">Perusahaan</option>
            </select></label
          >
          <label
            >Deposit awal<input
              type="number"
              min="0"
              v-model="admission.deposit_amount"
              placeholder="Rp 0"
          /></label>
          <label class="wide"
            >Diagnosis masuk<textarea
              rows="2"
              v-model="admission.admission_diagnosis"
              placeholder="Diagnosis atau alasan pasien dirawat"
            ></textarea>
          </label>
          <label class="wide"
            >Alergi / kewaspadaan<textarea
              rows="2"
              v-model="admission.allergy_notes"
              placeholder="Alergi obat, risiko jatuh, atau kewaspadaan lain"
            ></textarea>
          </label>
        </div>
        <div class="dialog-actions">
          <button class="outline" @click="closeModal">Batal</button
          ><button class="primary" :disabled="saving" @click="saveAdmission">
            <i class="fas fa-save"></i> Simpan Admisi
          </button>
        </div>
      </div>
    </div>

    <div
      class="modal-shell"
      v-if="modal === 'patient'"
      @click.self="closeModal"
    >
      <div class="dialog patient-dialog">
        <div class="patient-head">
          <div>
            <span>{{ detail.admission_number }}</span>
            <h2>{{ detail.patient_name }}</h2>
            <p>
              <i class="fas fa-bed"></i> {{ detail.bed && detail.bed.name }} ·
              <i class="fas fa-user-md"></i>
              {{ detail.attending_doctor_name || "-" }}
            </p>
          </div>
          <button @click="closeModal">×</button>
        </div>
        <nav>
          <button
            v-for="t in patientTabs"
            :key="t.id"
            :class="{ active: patientTab === t.id }"
            @click="patientTab = t.id"
          >
            <i :class="t.icon"></i>{{ t.label }}
          </button>
        </nav>
        <div class="panel" v-if="patientTab === 'summary'">
          <div class="info-grid">
            <article>
              <small>Diagnosis masuk</small
              ><b>{{ detail.admission_diagnosis || "-" }}</b>
            </article>
            <article>
              <small>Mulai dirawat</small
              ><b>{{ dateTime(detail.admitted_at) }}</b>
            </article>
            <article>
              <small>Penjamin</small><b>{{ label(detail.payment_type) }}</b>
            </article>
            <article>
              <small>Estimasi tagihan order</small
              ><b>{{ money(detail.running_bill) }}</b>
            </article>
          </div>
          <div class="billing-card">
            <div>
              <span>FINANCIAL CLEARANCE</span>
              <h4>Invoice Rawat Inap</h4>
              <p>{{ billingMessage }}</p>
            </div>
            <div class="billing-values">
              <small
                >Total<strong>{{
                  money(detail.billing && detail.billing.total)
                }}</strong></small
              ><small
                >Terbayar<strong>{{
                  money(detail.billing && detail.billing.paid)
                }}</strong></small
              ><small
                >Sisa<strong
                  :class="{
                    unpaid: detail.billing && detail.billing.unpaid > 0,
                  }"
                  >{{ money(detail.billing && detail.billing.unpaid) }}</strong
                ></small
              >
            </div>
            <div class="billing-actions">
              <button
                class="outline"
                v-if="detail.billing && detail.billing.payment_id"
                @click="
                  $router.push('/payment/detail/' + detail.billing.payment_id)
                "
              >
                <i class="fas fa-file-invoice"></i> Lihat Invoice</button
              ><button class="primary" :disabled="saving" @click="syncInvoice">
                <i class="fas fa-sync-alt"></i>
                {{
                  detail.billing && detail.billing.payment_id
                    ? "Sinkronkan"
                    : "Buat Invoice"
                }}
              </button>
            </div>
          </div>
          <h4>Aktivitas klinis terbaru</h4>
          <div class="timeline">
            <article v-for="n in detail.notes" :key="n.id">
              <i class="fas fa-notes-medical"></i>
              <div>
                <b>{{ label(n.note_type) }} · {{ n.created_by_name }}</b
                ><small>{{ dateTime(n.recorded_at) }}</small>
                <p>
                  {{
                    n.assessment ||
                    n.objective ||
                    n.subjective ||
                    "Catatan tanda vital"
                  }}
                </p>
              </div>
            </article>
            <p v-if="!detail.notes || !detail.notes.length" class="muted">
              Belum ada catatan klinis.
            </p>
          </div>
        </div>
        <div class="panel" v-if="patientTab === 'notes'">
          <div class="form-grid">
            <label
              >Jenis catatan<select v-model="note.note_type">
                <option value="nursing">Keperawatan</option>
                <option value="doctor">Dokter / CPPT</option>
                <option value="vital_sign">Tanda vital</option>
                <option value="handover">Handover</option>
                <option value="discharge_plan">Rencana pulang</option>
              </select></label
            ><label
              >Waktu<input
                type="datetime-local"
                v-model="note.recorded_at" /></label
            ><label class="wide"
              >S - Subjective<textarea
                v-model="note.subjective"
                rows="2"
              ></textarea></label
            ><label class="wide"
              >O - Objective<textarea
                v-model="note.objective"
                rows="2"
              ></textarea></label
            ><label
              >A - Assessment<textarea
                v-model="note.assessment"
                rows="2"
              ></textarea></label
            ><label
              >P - Plan<textarea v-model="note.plan" rows="2"></textarea>
            </label>
          </div>
          <div class="dialog-actions">
            <button class="primary" @click="saveNote">
              <i class="fas fa-save"></i> Simpan Catatan
            </button>
          </div>
        </div>
        <div class="panel" v-if="patientTab === 'orders'">
          <div class="form-grid">
            <label
              >Jenis order<select v-model="order.order_type">
                <option value="medicine">Obat</option>
                <option value="laboratory">Laboratorium</option>
                <option value="medical_action">Tindakan medis</option>
                <option value="diet">Diet</option>
                <option value="other">Lainnya</option>
              </select></label
            ><label
              >Nama item<input
                v-model="order.item_name"
                placeholder="Nama obat, lab, atau tindakan" /></label
            ><label
              >Jumlah<input
                type="number"
                min="0.01"
                step="0.01"
                v-model="order.quantity" /></label
            ><label
              >Harga satuan<input
                type="number"
                min="0"
                v-model="order.unit_price" /></label
            ><label class="wide"
              >Instruksi<textarea
                v-model="order.instruction"
                rows="2"
              ></textarea>
            </label>
          </div>
          <div class="dialog-actions">
            <button class="primary" @click="saveOrder">
              <i class="fas fa-plus"></i> Tambah Order
            </button>
          </div>
          <table class="mini">
            <thead>
              <tr>
                <th>Item</th>
                <th>Jenis</th>
                <th>Jumlah</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="o in detail.orders" :key="o.id">
                <td>{{ o.item_name }}</td>
                <td>{{ label(o.order_type) }}</td>
                <td>{{ o.quantity }}</td>
                <td>
                  <span class="status">{{ label(o.status) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="panel" v-if="patientTab === 'transfer'">
          <div class="form-grid">
            <label
              >Bed tujuan<select v-model="transfer.bed_id">
                <option value="">Pilih bed tersedia</option>
                <option v-for="b in candidates.beds" :value="b.id" :key="b.id">
                  {{ b.name }} · {{ b.category }}
                </option>
              </select></label
            ><label
              >Alasan pindah<input
                v-model="transfer.reason"
                placeholder="Kebutuhan klinis / operasional"
            /></label>
          </div>
          <div class="dialog-actions">
            <button class="primary" @click="saveTransfer">
              <i class="fas fa-exchange-alt"></i> Konfirmasi Pindah
            </button>
          </div>
        </div>
        <div class="panel" v-if="patientTab === 'discharge'">
          <div class="discharge-alert">
            <i class="fas fa-info-circle"></i> Pastikan rekonsiliasi obat,
            edukasi pasien, kontrol, dan penyelesaian tagihan sudah
            diverifikasi.
          </div>
          <div class="form-grid">
            <label
              >Waktu pulang<input
                type="datetime-local"
                v-model="discharge.discharged_at" /></label
            ><label class="wide"
              >Diagnosis pulang<textarea
                rows="2"
                v-model="discharge.discharge_diagnosis"
              ></textarea></label
            ><label class="wide"
              >Instruksi, obat pulang & rencana kontrol<textarea
                rows="3"
                v-model="discharge.discharge_instruction"
              ></textarea>
            </label>
          </div>
          <div class="dialog-actions">
            <button class="danger" @click="saveDischarge">
              <i class="fas fa-sign-out-alt"></i> Pulangkan Pasien
            </button>
          </div>
        </div>
      </div>
    </div>

    <ModalPatient
      source="inpatient-admission"
      @chosenPatient="setAdmissionPatient"
      @submitedPatient="setAdmissionPatient"
    />
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import ModalPatient from "@/component/general/ModalPatient.vue";
const localDate = () =>
  new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
export default {
  components: { ModalPatient },
  data: () => ({
    summary: {},
    patients: [],
    search: "",
    modal: "",
    saving: false,
    candidates: { doctors: [], beds: [] },
    selectedAdmissionPatient: null,
    detail: {},
    patientTab: "summary",
    patientTabs: [
      { id: "summary", label: "Ringkasan", icon: "fas fa-clipboard-list" },
      { id: "notes", label: "Catatan Klinis", icon: "fas fa-notes-medical" },
      { id: "orders", label: "Order", icon: "fas fa-prescription" },
      { id: "transfer", label: "Pindah Bed", icon: "fas fa-exchange-alt" },
      { id: "discharge", label: "Pulang", icon: "fas fa-sign-out-alt" },
    ],
    admission: {},
    note: {},
    order: {},
    transfer: {},
    discharge: {},
  }),
  computed: {
    filtered() {
      const s = this.search.toLowerCase();
      return this.patients.filter(
        (p) =>
          !s ||
          [p.patient_name, p.admission_number, p.doctor_name, p.bed_name]
            .join(" ")
            .toLowerCase()
            .includes(s)
      );
    },
    billingMessage() {
      const b = this.detail.billing || {};
      if (b.status === "paid")
        return "Financial clearance selesai. Pasien dapat diproses pulang.";
      if (b.status === "guaranteed")
        return "Tagihan dijamin BPJS/perusahaan dan siap untuk proses klaim.";
      if (b.payment_id)
        return "Invoice aktif. Selesaikan pembayaran atau validasi penjamin sebelum pulang.";
      return "Order belum disinkronkan ke modul Invoice & Pembayaran.";
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Pelayanan Pasien" },
      { title: "Rawat Inap" },
    ]);
    this.load();
  },
  methods: {
    async load() {
      const r = await ApiService.get("inpatient/dashboard");
      this.summary = r.data.summary || {};
      this.patients = r.data.patients || [];
    },
    async loadCandidates() {
      const r = await ApiService.get("inpatient/candidates?include_patients=0");
      this.candidates = r.data;
    },
    async openAdmission() {
      await this.loadCandidates();
      this.selectedAdmissionPatient = null;
      this.admission = {
        patient_id: "",
        attending_doctor_id: "",
        attending_doctor_name: "",
        bed_id: "",
        admitted_at: localDate(),
        admission_source: "outpatient",
        payment_type: "general",
        deposit_amount: 0,
        admission_diagnosis: "",
        allergy_notes: "",
      };
      this.modal = "admission";
    },
    openAdmissionPatientLookup() {
      this.$bvModal.show("modal-patient");
    },
    setAdmissionPatient(patient) {
      if (!patient || !patient.id) return;
      this.selectedAdmissionPatient = patient;
      this.admission.patient_id = patient.id;
      if (patient.allergy) this.admission.allergy_notes = patient.allergy;
    },
    admissionPatientIdentity(patient) {
      const medicalRecord =
        patient.local_medical_record_number ||
        patient.patient_id ||
        patient.medical_record_number ||
        patient.medical_record;
      const nik = patient.id_card_number || patient.nik;
      return [medicalRecord && `RM ${medicalRecord}`, nik && `NIK ${nik}`]
        .filter(Boolean)
        .join(" · ") || `ID ${patient.id}`;
    },
    syncDoctor() {
      const d = this.candidates.doctors.find(
        (x) => x.id == this.admission.attending_doctor_id
      );
      this.admission.attending_doctor_name = d ? d.name : "";
    },
    async saveAdmission() {
      if (!this.admission.patient_id || !this.admission.bed_id)
        return this.toast("Pasien dan bed wajib dipilih.", "danger");
      this.saving = true;
      try {
        await ApiService.post("inpatient/admissions", this.admission);
        this.closeModal();
        await this.load();
        this.toast("Admisi rawat inap berhasil disimpan.", "success");
      } catch (e) {
        this.toast(this.error(e), "danger");
      } finally {
        this.saving = false;
      }
    },
    async openPatient(p) {
      await this.loadCandidates();
      const r = await ApiService.get(`inpatient/admissions/${p.id}`);
      this.detail = r.data;
      this.patientTab = "summary";
      this.note = {
        note_type: "nursing",
        recorded_at: localDate(),
        subjective: "",
        objective: "",
        assessment: "",
        plan: "",
      };
      this.order = {
        order_type: "medicine",
        item_name: "",
        quantity: 1,
        unit_price: 0,
        instruction: "",
      };
      this.transfer = { bed_id: "", reason: "" };
      this.discharge = {
        discharged_at: localDate(),
        discharge_diagnosis: "",
        discharge_instruction: "",
      };
      this.modal = "patient";
    },
    async refreshDetail() {
      const r = await ApiService.get(`inpatient/admissions/${this.detail.id}`);
      this.detail = r.data;
    },
    async syncInvoice() {
      this.saving = true;
      try {
        await ApiService.post(`billing/inpatient/${this.detail.id}/accrue-bed`, {
          through_date: window.medhopToday(),
        });
        await ApiService.post(
          `inpatient/admissions/${this.detail.id}/invoice`,
          {}
        );
        await this.refreshDetail();
        this.toast(
          "Tarif bed harian dan order rawat inap berhasil disinkronkan tanpa duplikasi.",
          "success"
        );
      } catch (e) {
        this.toast(this.error(e), "danger");
      } finally {
        this.saving = false;
      }
    },
    async saveNote() {
      try {
        await ApiService.post(
          `inpatient/admissions/${this.detail.id}/notes`,
          this.note
        );
        await this.refreshDetail();
        this.patientTab = "summary";
        this.toast("Catatan klinis disimpan.", "success");
      } catch (e) {
        this.toast(this.error(e), "danger");
      }
    },
    async saveOrder() {
      try {
        await ApiService.post(
          `inpatient/admissions/${this.detail.id}/orders`,
          this.order
        );
        await this.refreshDetail();
        this.order = {
          order_type: "medicine",
          item_name: "",
          quantity: 1,
          unit_price: 0,
          instruction: "",
        };
        this.toast("Order pelayanan ditambahkan.", "success");
      } catch (e) {
        this.toast(this.error(e), "danger");
      }
    },
    async saveTransfer() {
      try {
        await ApiService.post(
          `inpatient/admissions/${this.detail.id}/transfer`,
          this.transfer
        );
        await this.load();
        await this.refreshDetail();
        await this.loadCandidates();
        this.patientTab = "summary";
        this.toast("Perpindahan bed berhasil dicatat.", "success");
      } catch (e) {
        this.toast(this.error(e), "danger");
      }
    },
    async saveDischarge() {
      if (
        !this.discharge.discharge_diagnosis ||
        !this.discharge.discharge_instruction
      )
        return this.toast(
          "Diagnosis dan instruksi pulang wajib diisi.",
          "danger"
        );
      try {
        await ApiService.post(
          `inpatient/admissions/${this.detail.id}/discharge`,
          this.discharge
        );
        this.closeModal();
        await this.load();
        this.toast("Pasien berhasil dipulangkan.", "success");
      } catch (e) {
        this.toast(this.error(e), "danger");
      }
    },
    closeModal() {
      this.modal = "";
    },
    toast(m, v) {
      this.$bvToast.toast(m, {
        title: v === "success" ? "Berhasil" : "Periksa kembali",
        variant: v,
        solid: true,
      });
    },
    error(e) {
      return (
        (e.response &&
          e.response.data &&
          (e.response.data.message || e.response.data.error)) ||
        "Proses belum dapat diselesaikan."
      );
    },
    dateTime(v) {
      return v
        ? new Date(v).toLocaleString("id-ID", {
            dateStyle: "medium",
            timeStyle: "short",
          })
        : "-";
    },
    label(v) {
      return String(v || "-")
        .replaceAll("_", " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    money(v) {
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0,
      }).format(v || 0);
    },
  },
};
</script>

<style scoped>
.ri-page {
  padding-bottom: 25px;
}
.primary,
.outline,
.danger {
  border: 0;
  border-radius: 10px;
  padding: 11px 16px;
  font-weight: 750;
}
.primary {
  background: linear-gradient(135deg, #14578b, #159b91);
  color: #fff;
}
.outline {
  background: #fff;
  border: 1px solid #cfdee7;
  color: #425b72;
}
.danger {
  background: #e5484d;
  color: #fff;
}
.kpis {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin: 15px 0;
}
.kpis article,
.card {
  background: #fff;
  border: 1px solid #dde7ed;
  border-radius: 15px;
  box-shadow: 0 5px 18px rgba(31, 57, 74, 0.05);
}
.kpis article {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 15px;
}
.kpis i {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border-radius: 11px;
}
.blue {
  background: #e6f4ff;
  color: #146aa2;
}
.purple {
  background: #f0eaff;
  color: #7048c8;
}
.green {
  background: #e8f8f2;
  color: #078562;
}
.amber {
  background: #fff4db;
  color: #b87300;
}
.red {
  background: #ffecec;
  color: #dc3f4a;
}
.kpis small,
.kpis strong {
  display: block;
}
.kpis small {
  color: #758799;
}
.kpis strong {
  font-size: 20px;
  color: #203549;
}
.card {
  overflow: hidden;
}
.card header {
  padding: 17px 19px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e6edf2;
}
.eyebrow,
.dialog-head span,
.patient-head span {
  font-size: 10px;
  color: #0b928b;
  font-weight: 800;
  letter-spacing: 0.11em;
}
.card h3 {
  font-size: 17px;
  margin: 2px 0;
}
.card header p {
  font-size: 11px;
  color: #7a8b9c;
  margin: 0;
}
.search {
  position: relative;
}
.search i {
  position: absolute;
  left: 13px;
  top: 50%;
  width: 18px;
  height: 18px;
  display: grid;
  place-items: center;
  margin: 0;
  transform: translateY(-50%);
  line-height: 1;
  pointer-events: none;
  color: #0a8781;
}
.search input {
  width: 310px;
  min-width: 0;
  box-sizing: border-box;
  border: 1px solid #d3e0e8;
  border-radius: 10px;
  padding: 11px 12px 11px 44px;
}
.table-wrap {
  overflow: auto;
}
table {
  width: 100%;
  border-collapse: collapse;
}
th {
  background: #f5f8fa;
  color: #5e7388;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-align: left;
  padding: 12px;
}
td {
  padding: 13px 12px;
  border-top: 1px solid #edf2f5;
  color: #344a5f;
  font-size: 13px;
}
td b,
td small {
  display: block;
}
td small {
  color: #8090a0;
  margin-top: 3px;
  font-size: 11px;
}
.payer,
.status {
  display: inline-flex;
  border-radius: 7px;
  padding: 5px 8px;
  font-weight: 750;
  font-size: 11px;
  background: #eef3f6;
}
.status.admitted {
  background: #e7f3ff;
  color: #146ba3;
}
.status.discharge_planning {
  background: #fff3d7;
  color: #9b6500;
}
.action {
  border: 0;
  background: #e5f4f3;
  color: #087e78;
  border-radius: 8px;
  padding: 8px 10px;
  font-weight: 750;
}
.empty {
  text-align: center;
  padding: 48px !important;
}
.empty i,
.empty b,
.empty span {
  display: block;
}
.empty i {
  font-size: 26px;
  color: #aab9c5;
  margin-bottom: 9px;
}
.empty span {
  font-size: 11px;
  color: #8394a4;
  margin-top: 4px;
}
.modal-shell {
  position: fixed;
  z-index: 1060;
  inset: 0;
  background: rgba(20, 39, 54, 0.55);
  display: grid;
  place-items: center;
  padding: 18px;
}
.dialog {
  background: #fff;
  border-radius: 17px;
  box-shadow: 0 24px 60px rgba(12, 31, 44, 0.25);
  max-height: 92vh;
  overflow: auto;
}
.admission-dialog {
  width: min(820px, 100%);
}
.patient-dialog {
  width: min(1020px, 100%);
}
.dialog-head,
.patient-head {
  display: flex;
  align-items: center;
  padding: 18px 20px;
  border-bottom: 1px solid #e4ecf1;
}
.head-icon {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: 11px;
  background: #e7f6f4;
  color: #07867f;
  margin-right: 11px;
  font-size: 18px;
}
.dialog-head h3,
.patient-head h2 {
  margin: 2px 0 0;
  font-size: 18px;
}
.dialog-head > button,
.patient-head > button {
  margin-left: auto;
  border: 0;
  background: #edf3f6;
  color: #557086;
  border-radius: 9px;
  width: 36px;
  height: 36px;
  font-size: 22px;
}
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 13px;
  padding: 18px 20px;
}
.form-grid label {
  font-size: 12px;
  font-weight: 750;
  color: #40576b;
}
.form-field__label {
  display: block;
  font-size: 12px;
  font-weight: 750;
  color: #40576b;
}
.form-grid input,
.form-grid select,
.form-grid textarea {
  width: 100%;
  display: block;
  margin-top: 5px;
  border: 1px solid #d3e0e8;
  border-radius: 9px;
  padding: 10px;
  background: #fff;
  color: #32485b;
}
.form-grid .wide {
  grid-column: 1/-1;
}
.patient-lookup-field {
  width: 100%;
  min-height: 62px;
  display: flex;
  align-items: center;
  gap: 11px;
  margin-top: 5px;
  padding: 9px 11px;
  border: 1px solid #c8d9e4;
  border-radius: 12px;
  background: #fff;
  color: #32485b;
  text-align: left;
  box-shadow: 0 4px 12px rgba(30, 64, 92, 0.05);
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background 0.15s ease;
}
.patient-lookup-field:hover,
.patient-lookup-field:focus-visible {
  border-color: #15958e;
  background: #fbfefe;
  box-shadow: 0 0 0 3px rgba(21, 149, 142, 0.13);
  outline: none;
}
.patient-lookup-field.is-selected {
  border-color: #85c8c3;
  background: #f7fcfb;
}
.patient-lookup-field__icon {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  background: #e6f5f3;
  color: #087e78;
  font-size: 17px;
}
.patient-lookup-field__content {
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
}
.patient-lookup-field__content strong {
  overflow: hidden;
  color: #24394d;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.patient-lookup-field__content small {
  overflow: hidden;
  margin-top: 2px;
  color: #718597;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.patient-lookup-field__action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  padding: 8px 10px;
  border-radius: 9px;
  background: #14658d;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
}
.patient-lookup-field__action i {
  color: inherit !important;
  background-image: none !important;
  -webkit-background-clip: border-box !important;
  background-clip: border-box !important;
  -webkit-text-fill-color: currentColor !important;
}
.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 20px 18px;
}
.patient-head {
  background: linear-gradient(120deg, #f0f8f8, #f4f8fc);
}
.patient-head p {
  margin: 5px 0 0;
  color: #677b8e;
}
.patient-head p i {
  color: #078b83;
  margin-right: 3px;
}
.patient-dialog nav {
  display: flex;
  gap: 4px;
  padding: 8px 15px;
  border-bottom: 1px solid #e3ecf1;
}
.patient-dialog nav button {
  border: 0;
  background: transparent;
  border-radius: 8px;
  padding: 10px 13px;
  color: #687c8f;
  font-weight: 700;
}
.patient-dialog nav button.active {
  background: #e8f5f3;
  color: #087f79;
}
.patient-dialog nav i {
  margin-right: 6px;
}
.panel {
  min-height: 300px;
  padding: 18px 20px;
}
.panel > .form-grid {
  padding: 0;
}
.info-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 20px;
}
.info-grid article {
  padding: 13px;
  border: 1px solid #e0e9ee;
  border-radius: 11px;
  background: #f8fafb;
}
.info-grid small,
.info-grid b {
  display: block;
}
.info-grid small {
  color: #7b8d9e;
  margin-bottom: 5px;
}
.timeline article {
  display: flex;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #edf2f5;
}
.timeline i {
  width: 34px;
  height: 34px;
  background: #e9f5f4;
  color: #07847d;
  display: grid;
  place-items: center;
  border-radius: 9px;
}
.timeline small {
  display: block;
  color: #7f90a0;
}
.timeline p {
  margin: 5px 0;
}
.muted {
  color: #8091a1;
}
.mini {
  margin-top: 15px;
  border: 1px solid #e2eaef;
}
.discharge-alert {
  background: #fff5da;
  color: #875d00;
  border: 1px solid #ffe0a0;
  border-radius: 10px;
  padding: 12px;
}
.discharge-alert + .form-grid {
  padding: 15px 0;
}
@media (max-width: 900px) {
  .kpis {
    grid-template-columns: repeat(2, 1fr);
  }
  .info-grid {
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 600px) {
  .kpis,
  .form-grid,
  .info-grid {
    grid-template-columns: 1fr;
  }
  .form-grid .wide {
    grid-column: auto;
  }
  .card header {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }
  .search,
  .search input {
    width: 100%;
  }
  .patient-dialog nav {
    overflow: auto;
  }
  .patient-dialog nav button {
    white-space: nowrap;
  }
  .modal-shell {
    padding: 7px;
  }
  .patient-lookup-field__action span {
    display: none;
  }
  .dialog {
    max-height: 97vh;
  }
  .kpis article {
    padding: 12px;
  }
}
.billing-card {
  display: grid;
  grid-template-columns: 1.4fr 1fr auto;
  gap: 16px;
  align-items: center;
  padding: 15px;
  margin-bottom: 20px;
  border: 1px solid #cfe4e2;
  background: linear-gradient(120deg, #f0f9f8, #f8fbfd);
  border-radius: 13px;
}
.billing-card span {
  font-size: 9px;
  letter-spacing: 0.12em;
  color: #07867f;
  font-weight: 850;
}
.billing-card h4 {
  margin: 3px 0;
}
.billing-card p {
  margin: 0;
  color: #687d8f;
  font-size: 11px;
}
.billing-values {
  display: flex;
  gap: 18px;
}
.billing-values small,
.billing-values strong {
  display: block;
}
.billing-values strong {
  font-size: 13px;
  margin-top: 3px;
}
.billing-values .unpaid {
  color: #df3e48;
}
.billing-actions {
  display: flex;
  gap: 7px;
}
@media (max-width: 900px) {
  .billing-card {
    grid-template-columns: 1fr;
  }
  .billing-actions {
    justify-content: flex-end;
  }
}
@media (max-width: 600px) {
  .billing-values {
    justify-content: space-between;
  }
  .billing-actions {
    flex-direction: column;
  }
}
</style>
